/**
 * Utility functions for HER (Home Energy Report) templates
 * 
 * HER templates are rendered as full React apps that require iframe embedding
 * because they need their own JavaScript runtime to execute.
 * 
 * The preview URL is now returned by the render API (/v1/cx/render) with
 * the `herPreviewUrl` field in the response.
 * 
 * HER templateKey format: "HER.{REPORT_TYPE}.{FUEL_TYPE}"
 * Example: "HER.HER_MONTHLY_REPORT.ELECTRIC"
 */

/**
 * Check if a templateKey is an HER template
 */
export function isHerTemplate(templateKey: string | null | undefined): boolean {
  return !!templateKey && templateKey.startsWith('HER.');
}

/**
 * Extract report type from HER templateKey
 * Example: "HER.HER_MONTHLY_REPORT.ELECTRIC" → "HER_MONTHLY_REPORT"
 */
export function extractHerReportType(templateKey: string | null | undefined): string {
  if (!templateKey || !isHerTemplate(templateKey)) {
    return 'HER_MONTHLY_REPORT'; // default
  }
  const parts = templateKey.split('.');
  return parts[1] || 'HER_MONTHLY_REPORT';
}

/**
 * Extract fuel type from HER templateKey
 * Example: "HER.HER_MONTHLY_REPORT.ELECTRIC" → "ELECTRIC"
 * Example: "HER.HER_MONTHLY_REPORT.ELECTRIC,GAS" → "ELECTRIC,GAS"
 */
export function extractHerFuelType(templateKey: string | null | undefined): string {
  if (!templateKey || !isHerTemplate(templateKey)) {
    return 'ELECTRIC'; // default
  }
  const parts = templateKey.split('.');
  return parts[2]?.toUpperCase() || 'ELECTRIC';
}

/**
 * Extract badge label for HER templates based on fuel type
 * Used for displaying fuel type chips in UI
 * 
 * Examples:
 * - "HER.HER_MONTHLY_REPORT.ELECTRIC" → "EL"
 * - "HER.HER_MONTHLY_REPORT.GAS" → "GAS"
 * - "HER.HER_MONTHLY_REPORT.ELECTRIC,GAS" → "DF" (Dual Fuel)
 * - "HER.HER_MONTHLY_REPORT.WATER" → "WTR"
 */
export function extractHerBadgeLabel(templateKey: string | null | undefined): string {
  if (!templateKey || !isHerTemplate(templateKey)) {
    return 'EL'; // default
  }
  
  const fuelType = extractHerFuelType(templateKey);
  
  // Check if it contains comma (multiple fuel types = Dual Fuel)
  if (fuelType.includes(',')) {
    return 'DF';
  }
  
  // Map single fuel types to badge labels
  if (fuelType === 'ELECTRIC') return 'EL';
  if (fuelType === 'GAS') return 'GAS';
  if (fuelType === 'WATER') return 'WTR';
  
  // Fallback: return first 3 characters or 'EL'
  return fuelType.length > 0 ? fuelType.substring(0, 3) : 'EL';
}

/**
 * Extract badge label with parentheses for display in toolbar
 * Example: "HER.HER_MONTHLY_REPORT.ELECTRIC" → "(EL)"
 */
export function extractHerBadgePrefix(templateKey: string | null | undefined): string {
  const badge = extractHerBadgeLabel(templateKey);
  return `(${badge})`;
}

/**
 * Format HER report type as display name
 * Converts SNAKE_CASE to Title Case
 * 
 * Example: "HER_MONTHLY_REPORT" → "Her Monthly Report"
 */
export function formatHerDisplayName(reportType: string): string {
  return reportType
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Get display name from HER templateKey
 * Combines extractHerReportType and formatHerDisplayName
 * 
 * Example: "HER.HER_MONTHLY_REPORT.ELECTRIC" → "Her Monthly Report"
 */
export function getHerDisplayName(templateKey: string | null | undefined): string {
  const reportType = extractHerReportType(templateKey);
  return formatHerDisplayName(reportType);
}


/**
 * Preview-only CSS injected into the HER srcDoc.
 *
 * The real HER page fills the space right of the report pages with the
 * `.her-container` background (light blue) — intentional product behavior,
 * but noise in the editor preview. Proxypulse locks each element's
 * background-color with `!important` via `[data-her-preview-id="…"]` rules,
 * so this override must also be `!important`, injected after that style tag
 * (end of <head>), and at least as specific.
 */
const HER_PREVIEW_OVERRIDE_CSS = [
  // Shrink-wrap the container to its .page children so the blue filler
  // region disappears; whiten as fallback for any remaining exposed area.
  '.her-container[data-her-preview-id]{width:fit-content !important;min-width:0 !important;background-color:#ffffff !important;}',
  'html,body{background-color:#ffffff !important;}',
].join('');

/**
 * Prepare HER HTML for rendering inside an iframe via `srcDoc`.
 *
 * Proxypulse already handles: script stripping, stylesheet inlining, font
 * inlining, and absolutizing all asset URLs.
 *
 * Client-side cleanup:
 * 1. Strip <noscript> tags. Because the iframe sandbox has no allow-scripts,
 *    the browser treats JS as disabled and renders <noscript> content (e.g.
 *    "You need to enable JavaScript to run this app."). Scripts are already
 *    stripped by proxypulse, so noscript is noise.
 * 2. Inject preview-only style overrides (see HER_PREVIEW_OVERRIDE_CSS).
 */
export function prepareHerSrcDoc(html: string, _herPreviewUrl: string): string {
  const cleaned = html.replace(/<noscript\b[^>]*>[\s\S]*?<\/noscript>/gi, '');
  const styleTag = `<style data-her-preview-ui-overrides="true">${HER_PREVIEW_OVERRIDE_CSS}</style>`;
  return cleaned.includes('</head>')
    ? cleaned.replace('</head>', `${styleTag}</head>`)
    : styleTag + cleaned;
}
